import type { ProjectConfig, SectionKey } from "./types";
import { SECTION_ORDER } from "./types";

/** Deep equality via stable JSON. Sufficient for plain-data config objects. */
export function isEqual(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * Walk two configs and count "leaf differences" — every scalar / array /
 * object-leaf that differs counts as one change. Treats arrays as atomic
 * (one diff regardless of element count) which mirrors how users perceive
 * a chip-list edit: it's "one change" no matter how many chips they
 * added/removed in a row.
 */
export function countChanges(
  serverConfig: ProjectConfig,
  workingConfig: ProjectConfig,
): { changes: number; sections: number } {
  let changes = 0;
  let sections = 0;
  for (const key of SECTION_ORDER) {
    const sChanges = countSectionChanges(
      serverConfig[key],
      workingConfig[key],
    );
    if (sChanges > 0) {
      sections += 1;
      changes += sChanges;
    }
  }
  return { changes, sections };
}

function countSectionChanges(server: unknown, working: unknown): number {
  if (isEqual(server, working)) return 0;
  // Object sections are counted per-field; arrays / scalars count as one.
  if (
    server !== null &&
    typeof server === "object" &&
    !Array.isArray(server) &&
    working !== null &&
    typeof working === "object" &&
    !Array.isArray(working)
  ) {
    let n = 0;
    const keys = new Set([
      ...Object.keys(server as Record<string, unknown>),
      ...Object.keys(working as Record<string, unknown>),
    ]);
    for (const k of keys) {
      const sv = (server as Record<string, unknown>)[k];
      const wv = (working as Record<string, unknown>)[k];
      if (!isEqual(sv, wv)) n += 1;
    }
    return n;
  }
  return 1;
}

/** Was section X edited vs the server snapshot? */
export function isSectionDirty(
  serverConfig: ProjectConfig,
  workingConfig: ProjectConfig,
  key: SectionKey,
): boolean {
  return !isEqual(serverConfig[key], workingConfig[key]);
}

/**
 * Render a small humanized duration for the "= 1 hour" helper under TTL
 * inputs (mockup project-config.html L620). Pure & deterministic — no
 * Intl dependency.
 */
export function humanizeDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "—";
  if (seconds === 0) return "0 seconds";
  const day = 86400;
  const hour = 3600;
  const min = 60;
  if (seconds % day === 0) {
    const d = seconds / day;
    return `${d} day${d === 1 ? "" : "s"}`;
  }
  if (seconds % hour === 0) {
    const h = seconds / hour;
    return `${h} hour${h === 1 ? "" : "s"}`;
  }
  if (seconds % min === 0) {
    const m = seconds / min;
    return `${m} minute${m === 1 ? "" : "s"}`;
  }
  return `${seconds} second${seconds === 1 ? "" : "s"}`;
}

/**
 * Try to extract a 1-based line number from a JSON.parse error message.
 * `JSON.parse` in V8 surfaces "position N" rather than line numbers; we
 * scan the input up to that position to compute the line. Falls back to
 * the bare error message if a position can't be found.
 */
export function describeJsonError(input: string, err: unknown): string {
  const message = err instanceof Error ? err.message : String(err);
  const m = /position\s+(\d+)/i.exec(message);
  if (!m) return `Invalid JSON: ${message}`;
  const pos = Number(m[1]);
  if (!Number.isFinite(pos)) return `Invalid JSON: ${message}`;
  const slice = input.slice(0, pos);
  const line = slice.split("\n").length;
  return `Invalid JSON: line ${line} — ${message}`;
}

/**
 * Numeric range validator returning a helper string if out of bounds, or
 * null when valid. NaN counts as "required" since the user blanked the
 * input.
 */
export function rangeError(
  value: number,
  min: number,
  max: number,
): string | null {
  if (!Number.isFinite(value)) return "Required";
  if (value < min) return `Must be ≥ ${min}`;
  if (value > max) return `Must be ≤ ${max}`;
  return null;
}
